import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  APP_VERSION, CHANGELOG_VERSION, CHANGELOG, PRIVACY_POLICY, TERMS,
  isAgreed, markAgreed, itemVisibleToRole,
} from "@/lib/versions";
import { CheckCircle, Info, Sparkle, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

/**
 * Sequentially prompts the user to accept:
 * 1) Changelog (if CHANGELOG_VERSION changed)
 * 2) Privacy Policy (if PRIVACY_VERSION changed)
 * 3) Terms & Conditions (if TERMS_VERSION changed)
 * Each acceptance is stored per-user in localStorage.
 */
export default function AgreementsModal() {
  const { user } = useAuth();
  const uid = user?.id;
  const role = user?.role;

  // Skip changelog if no items are visible for this role in the latest release.
  const latestVisible = useMemo(() => {
    if (!role) return [];
    const latest = CHANGELOG[0];
    return (latest?.items || []).filter((it) => itemVisibleToRole(it, role));
  }, [role]);

  const needsChangelog = useMemo(
    () => !!uid && latestVisible.length > 0 && !isAgreed(uid, "changelog", CHANGELOG_VERSION),
    [uid, latestVisible]
  );
  const needsPrivacy   = useMemo(() => !!uid && !isAgreed(uid, "privacy", PRIVACY_POLICY.version), [uid]);
  const needsTerms     = useMemo(() => !!uid && !isAgreed(uid, "terms", TERMS.version), [uid]);

  const [step, setStep] = useState(null); // "changelog" | "privacy" | "terms" | null
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!uid) return;
    if (needsChangelog) setStep("changelog");
    else if (needsPrivacy) setStep("privacy");
    else if (needsTerms) setStep("terms");
    else setStep(null);
    setChecked(false);
  }, [uid, needsChangelog, needsPrivacy, needsTerms]);

  if (!uid || !step) return null;

  const accept = () => {
    if (step === "changelog") {
      markAgreed(uid, "changelog", CHANGELOG_VERSION);
      if (needsPrivacy) setStep("privacy");
      else if (needsTerms) setStep("terms");
      else setStep(null);
    } else if (step === "privacy") {
      markAgreed(uid, "privacy", PRIVACY_POLICY.version);
      if (needsTerms) setStep("terms");
      else setStep(null);
    } else if (step === "terms") {
      markAgreed(uid, "terms", TERMS.version);
      setStep(null);
    }
    setChecked(false);
  };

  return (
    <Dialog open={!!step} onOpenChange={() => { /* modal is blocking */ }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {step === "changelog" && <ChangelogBody role={user?.role} />}
        {step === "privacy" && <PolicyBody title="Privacy Policy" doc={PRIVACY_POLICY} icon={<ShieldCheck size={22} weight="duotone" className="text-[#fd7e14]" />} />}
        {step === "terms" && <PolicyBody title="Terms & Conditions" doc={TERMS} icon={<Info size={22} weight="duotone" className="text-[#fd7e14]" />} />}

        <label className="mt-3 flex items-start gap-2 text-sm">
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-0.5" data-testid={`agree-${step}-checkbox`} />
          <span>
            I have read and I agree to the{" "}
            <b>{step === "changelog" ? "latest platform updates" : step === "privacy" ? "SMIPL Privacy Policy" : "Terms & Conditions"}</b>.
          </span>
        </label>

        <DialogFooter className="mt-4">
          <Button onClick={accept} disabled={!checked} className="btn-brand" data-testid={`agree-${step}-continue`}>
            <CheckCircle size={16} className="mr-1.5" /> I Accept & Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangelogBody({ role }) {
  const latest = CHANGELOG[0];
  const latestItems = (latest.items || []).filter((it) => itemVisibleToRole(it, role));
  const olderEntries = CHANGELOG.slice(1)
    .map((entry) => ({ ...entry, items: (entry.items || []).filter((it) => itemVisibleToRole(it, role)) }))
    .filter((entry) => entry.items.length > 0);
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Sparkle size={22} weight="duotone" className="text-[#fd7e14]" />
          What&apos;s new — {latest.date}
        </DialogTitle>
      </DialogHeader>
      <p className="text-sm opacity-70">The platform has been updated. Please review the highlights below before continuing.</p>
      <div className="mt-3 rounded-md border p-4" style={{ borderColor: "hsl(var(--border))" }}>
        <div className="flex items-baseline justify-between mb-2">
          <div className="font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>Latest updates</div>
          <div className="text-xs opacity-60">{latest.date}</div>
        </div>
        <ul className="space-y-2 text-sm">
          {latestItems.map((it, i) => (
            <li key={i} className="flex gap-2"><span className="text-[#fd7e14]">•</span><span>{it.text}</span></li>
          ))}
        </ul>
      </div>
      {olderEntries.length > 0 && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer opacity-70 hover:opacity-100">See full change history</summary>
          <div className="mt-2 space-y-3">
            {olderEntries.map((entry) => (
              <div key={entry.version} className="rounded-md border p-3" style={{ borderColor: "hsl(var(--border))" }}>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="font-semibold text-sm">{entry.date}</div>
                </div>
                <ul className="space-y-1 text-xs">
                  {entry.items.map((it, i) => (
                    <li key={i} className="opacity-80">• {it.text}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </details>
      )}
    </>
  );
}

function PolicyBody({ title, doc, icon }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {icon}
          {title} <span className="text-xs font-normal opacity-60 ml-2">Last updated {doc.updated}</span>
        </DialogTitle>
      </DialogHeader>
      <div
        className="prose prose-sm dark:prose-invert max-w-none text-sm [&_h4]:mt-4 [&_h4]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_p]:mt-2"
        dangerouslySetInnerHTML={{ __html: doc.html }}
      />
    </>
  );
}
